import { AsyncPipe, NgClass } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { NodeImportInterface } from '@shared/api';
import { ButtonComponent } from '@shared/ui/button';
import { InfoComponent, InfoTypeEnum } from '@shared/ui/info';
import { ModalLoaderComponent } from '@shared/ui/modal-loader';
import { ModalCloseDirective } from '@shared/ui/modals';
import { NgScrollbar } from 'ngx-scrollbar';
import { filter, finalize } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SitesService } from '../../sites.service';
import { ImportNodesModalItemComponent } from './import-nodes-item/import-nodes-modal-item.component';
import {
  ImportNodesModalDataInterface,
  NodeImportRowInterface,
} from './import-nodes-modal.interface';

/**
 * TODO: Move it to the 'app' scope when sidebar is being refactored
 */
@Component({
  selector: 'shared-store-import-nodes-modal',
  imports: [
    MatDialogModule,
    ImportNodesModalItemComponent,
    ButtonComponent,
    NgClass,
    ModalCloseDirective,
    InfoComponent,
    NgScrollbar,
    ModalLoaderComponent,
    AsyncPipe,
  ],
  templateUrl: './import-nodes-modal.component.html',
  styleUrls: ['./import-nodes-modal.component.css'],
})
export class ImportNodesModalComponent {
  private readonly sitesService = inject(SitesService);

  dialogRef = inject<MatDialogRef<ImportNodesModalComponent>>(MatDialogRef);
  data: ImportNodesModalDataInterface = inject(MAT_DIALOG_DATA);

  @HostListener('window:keyup.Enter', ['$event'])
  onEnterClick(event: KeyboardEvent): void {
    this.import();
  }

  isSubmitting = signal(false);

  rows: WritableSignal<NodeImportRowInterface[]> = signal([]);

  private getSortedNodes(nodes: NodeImportInterface[]): NodeImportInterface[] {
    const notAddedNodes = nodes.filter(
      (node) => !node.addedInCurrentAccount && !node.addedInAnotherAccount,
    );
    const addedNodes = nodes.filter(
      (node) => node.addedInCurrentAccount && !node.addedInAnotherAccount,
    );
    const addedToAnotherAccount = nodes.filter(
      (node) => !node.addedInCurrentAccount && node.addedInAnotherAccount,
    );
    return notAddedNodes.concat(addedNodes).concat(addedToAnotherAccount);
  }

  rowsCount: Signal<number> = computed(() => this.rows().length);

  selectedNodes: Signal<NodeImportInterface[]> = computed(() =>
    this.rows()
      .filter((row) => row.isSelected)
      .map((row) => row.entity),
  );

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  constructor() {
    this.data.nodes
      .pipe(
        takeUntilDestroyed(),
        filter((nodes) => !!nodes),
        tap((nodes) => {
          this.rows.set(
            this.getSortedNodes(nodes).map((node) => ({
              entity: node,
              isSelected: false,
            })),
          );
          setTimeout(() => {
            this.createAddSiteLink();
          }, 1000);
        }),
      )
      .subscribe();
  }

  private createAddSiteLink(): void {
    const infoLink = document.querySelector('#info-with-handler .info__text a');

    infoLink?.classList.add('cursor-pointer');
    infoLink?.addEventListener('click', () => {
      this.close();
      this.sitesService.openAddSiteModal();
    });
  }

  close(): void {
    this.dialogRef.close(false);
  }

  import(): void {
    if (this.selectedNodes().length > 0 && !this.isSubmitting()) {
      this.isSubmitting.set(true);

      this.data
        .action(this.selectedNodes())
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.isSubmitting.set(false)),
        )
        .subscribe(() => this.dialogRef.close(true));
    }
  }

  toggle(toggledRow: NodeImportRowInterface): void {
    this.rows.update((rows) =>
      rows.map((row) =>
        row.entity.serviceTag === toggledRow.entity.serviceTag
          ? { ...row, isSelected: !row.isSelected }
          : row,
      ),
    );
  }

  selectAll(): void {
    this.rows.update((rows) =>
      rows.map((row) =>
        row.entity.addedInCurrentAccount || row.entity.addedInAnotherAccount
          ? row
          : { ...row, isSelected: true },
      ),
    );
  }

  deselectAll(): void {
    this.rows.update((rows) =>
      rows.map((row) => ({ ...row, isSelected: false })),
    );
  }

  protected readonly InfoTypeEnum = InfoTypeEnum;
}
